// Estimation de la qualité d'une image cible de réalité augmentée, sur l'image ramenée à WORK_SIDE px de grand côté.
// Ce n'est ni la note de Vuforia (application v4) ni celle du détecteur de l'application v5 : une approximation de ce qui les
// fait échouer — peu de coins (détecteur FAST, de la famille de ceux qu'ils emploient), peu de détails visibles de loin, coins
// mal répartis, coins qui se ressemblent entre eux, contraste faible une fois en gris.
//
// Les détails visibles de loin se comptent sur l'image entière, réduite comme la voit la caméra qui la cadre. La répartition et
// le contraste se mesurent sur la ZONE UTILE — le rectangle des cases de la grille qui portent des coins —, pas sur le fond vide
// qui l'entoure ; la part de l'image qu'occupe cette zone est un critère à part entière.
//
// Le barème est calé sur des cibles notées par Vuforia : une écorce couverte de détails et une clé recadrée au ras de ses bords
// à 5 étoiles ; un gland, un coffre et la même clé, petits au centre d'un grand fond blanc, à 4. Ce qui les sépare est la part
// de l'image qu'occupent les détails, plus que leur nombre ; un 5 étoiles n'est pas forcément sans défaut. À recaler sur
// d'autres cibles dont on connaît le comportement sur le terrain.
//
// `measureTarget` fait le calcul coûteux, une fois par image ; `rateTarget` en tire la note, le verdict et les alertes, en
// comparant la cible aux autres cibles de son module.

export const WORK_SIDE = 640 // taille de travail par défaut du détecteur v5
export const GRID = 8 // cases par côté pour la couverture

const FAST_THRESHOLD = 20 // écart de luminance au centre pour un coin FAST (celui d'ORB par défaut)
const CELL_MIN_CORNERS = 4 // coins qu'il faut à une case pour être couverte
const MAX_DESCRIBED = 300 // coins les plus forts comparés entre eux
const PATCH_RADIUS = 8 // vignette de 16 px autour d'un coin…
const PATCH_CELLS = 8 // … réduite à 8 × 8 moyennes
const MATCH_NCC = 0.9 // corrélation au-delà de laquelle deux vignettes se confondent
const MIN_MATCH_DISTANCE = 16 // deux coins plus proches appartiennent à la même structure
const FAR_SIDE = 160 // grand côté de l'image réduite où l'on compte les détails visibles de loin

// Barème : chaque critère retire au plus `max` points — tous à `bad`, aucun à `good`, linéairement entre les deux — et la note
// vaut 100 moins les points retirés. Un critère pénalisé est une alerte, qui dit ce qu'elle coûte : la note et les alertes ne
// peuvent pas se contredire.
const SCALE = {
  format: { max: 100 }, // l'application v4 n'accepte que le JPG
  'too-small': { bad: 240, good: 480, max: 70 }, // petit côté en px (Vuforia en demande 320 au moins)
  heavy: { bad: 4000, good: 2000, max: 10 }, // grand côté en px : environ 1000 suffisent, l'image est réduite avant d'être apprise
  stretched: { bad: 4, good: 2, max: 30 }, // rapport des côtés
  'few-corners': { bad: 50, good: 300, max: 50 }, // coins détectés
  'fine-details': { bad: 50, good: 200, max: 20 }, // coins qui restent sur l'image entière réduite à FAR_SIDE px
  'small-zone': { bad: 0.1, good: 0.9, max: 30 }, // part de l'image qu'occupe la zone utile
  'empty-zones': { bad: 0.05, good: 0.6, max: 30 }, // part des cases de la zone utile qui sont couvertes
  repetitive: { bad: 0, good: 0.5, max: 70 }, // part des coins qui ne se confondent pas entre eux
  'low-contrast': { bad: 15, good: 40, max: 40 }, // écart-type de la luminance
  similar: { bad: 0.6, good: 0.3, max: 50 } // part des coins retrouvés dans une autre cible du même module
}
const ERROR_PENALTY = 30 // pénalité à partir de laquelle un défaut est majeur
// Note minimale de chaque verdict, cinq niveaux comme les étoiles de Vuforia.
const VERDICTS = [
  [90, 'excellent'],
  [70, 'good'],
  [50, 'average'],
  [30, 'poor'],
  [0, 'bad']
]

// Cercle de Bresenham de rayon 3 du test FAST, dans l'ordre.
// prettier-ignore
const CIRCLE = [[0, -3], [1, -3], [2, -2], [3, -1], [3, 0], [3, 1], [2, 2], [1, 3], [0, 3], [-1, 3], [-2, 2], [-3, 1], [-3, 0], [-3, -1], [-2, -2], [-1, -3]]

/** Luminance BT.601 d'une image RGBA, la pondération du détecteur v5. */
export const toGray = rgba => {
  const gray = new Uint8ClampedArray(rgba.length / 4)
  for (let i = 0, p = 0; i < gray.length; i++, p += 4) gray[i] = 0.299 * rgba[p] + 0.587 * rgba[p + 1] + 0.114 * rgba[p + 2]
  return gray
}

// Somme des écarts au seuil des pixels du cercle situés du côté [sign] de [bound], si 9 au moins s'y suivent ; 0 sinon.
// Le tour fait 16 + 9 pas pour attraper un arc qui chevauche le début du cercle.
const arcScore = (gray, i, offsets, bound, sign) => {
  let run = 0
  let longest = 0
  let sum = 0
  for (let k = 0; k < 25; k++) {
    const excess = sign * (gray[i + offsets[k % 16]] - bound)
    if (excess <= 0) {
      run = 0
      continue
    }
    if (k < 16) sum += excess
    longest = Math.max(longest, ++run)
  }
  return longest >= 9 ? sum : 0
}

// Score FAST-9 d'un pixel, 0 s'il n'est pas un coin. Tout arc de 9 contient au moins 2 des 4 points cardinaux : test rapide d'abord.
const fastScore = (gray, i, offsets) => {
  const high = gray[i] + FAST_THRESHOLD
  const low = gray[i] - FAST_THRESHOLD
  let brighter = 0
  let darker = 0
  for (let k = 0; k < 16; k += 4) {
    const value = gray[i + offsets[k]]
    if (value > high) brighter++
    else if (value < low) darker++
  }
  if (brighter < 2 && darker < 2) return 0
  return Math.max(arcScore(gray, i, offsets, high, 1), arcScore(gray, i, offsets, low, -1))
}

/** Coins FAST-9 de l'image, après suppression des non-maxima sur 3 × 3. */
export const detectCorners = (gray, width, height) => {
  const offsets = CIRCLE.map(([x, y]) => y * width + x)
  const scores = new Float32Array(width * height)
  for (let y = 3; y < height - 3; y++) {
    for (let x = 3; x < width - 3; x++) scores[y * width + x] = fastScore(gray, y * width + x, offsets)
  }

  const corners = []
  for (let y = 4; y < height - 4; y++) {
    for (let x = 4; x < width - 4; x++) {
      const i = y * width + x
      const score = scores[i]
      if (!score) continue
      const isMax = [-width - 1, -width, -width + 1, -1, 1, width - 1, width, width + 1].every(d => scores[i + d] <= score)
      if (isMax) corners.push({ x, y, score })
    }
  }
  return corners
}

// Vignette normalisée (moyenne nulle, norme 1) autour d'un coin : 8 × 8 moyennes de blocs de 2 × 2 px.
const patchOf = (gray, width, cx, cy) => {
  const step = (2 * PATCH_RADIUS) / PATCH_CELLS
  const patch = new Float32Array(PATCH_CELLS * PATCH_CELLS)
  let mean = 0
  for (let j = 0; j < PATCH_CELLS; j++) {
    for (let i = 0; i < PATCH_CELLS; i++) {
      const left = cx - PATCH_RADIUS + i * step
      const top = cy - PATCH_RADIUS + j * step
      let sum = 0
      for (let dy = 0; dy < step; dy++) for (let dx = 0; dx < step; dx++) sum += gray[(top + dy) * width + left + dx]
      patch[j * PATCH_CELLS + i] = sum
      mean += sum
    }
  }
  mean /= patch.length
  let norm = 0
  for (let k = 0; k < patch.length; k++) {
    patch[k] -= mean
    norm += patch[k] * patch[k]
  }
  norm = Math.sqrt(norm) || 1
  for (let k = 0; k < patch.length; k++) patch[k] /= norm
  return patch
}

const correlation = (a, b) => {
  let dot = 0
  for (let k = 0; k < a.length; k++) dot += a[k] * b[k]
  return dot
}

// Les coins les plus forts, loin du bord, avec leur vignette.
const describe = (gray, width, height, corners) =>
  corners
    .filter(c => c.x >= PATCH_RADIUS && c.y >= PATCH_RADIUS && c.x < width - PATCH_RADIUS && c.y < height - PATCH_RADIUS)
    .sort((a, b) => b.score - a.score)
    .slice(0, MAX_DESCRIBED)
    .map(c => ({ x: c.x, y: c.y, patch: patchOf(gray, width, c.x, c.y) }))

// Part des coins décrits qu'aucun autre coin éloigné de la même image ne reproduit : une texture répétée les confond.
const distinctivenessOf = described => {
  if (described.length < 2) return 1
  const confused = new Set()
  described.forEach((a, i) => {
    for (let j = i + 1; j < described.length; j++) {
      const b = described[j]
      if (Math.hypot(a.x - b.x, a.y - b.y) < MIN_MATCH_DISTANCE || correlation(a.patch, b.patch) < MATCH_NCC) continue
      confused.add(i)
      confused.add(j)
    }
  })
  return 1 - confused.size / described.length
}

// Part des coins de [a] qu'on retrouve dans [b].
const sharedRatio = (a, b) => {
  if (!a.length || !b.length) return 0
  return a.filter(da => b.some(db => correlation(da.patch, db.patch) >= MATCH_NCC)).length / a.length
}

// Image réduite à [side] px de grand côté au plus, chaque pixel moyennant le bloc de l'image d'origine qu'il recouvre.
const downscale = (gray, width, height, side) => {
  const scale = Math.min(1, side / Math.max(width, height))
  const w = Math.max(1, Math.round(width * scale))
  const h = Math.max(1, Math.round(height * scale))
  const small = new Uint8ClampedArray(w * h)
  for (let y = 0; y < h; y++) {
    const top = Math.floor((y * height) / h)
    const bottom = Math.max(top + 1, Math.floor(((y + 1) * height) / h))
    for (let x = 0; x < w; x++) {
      const left = Math.floor((x * width) / w)
      const right = Math.max(left + 1, Math.floor(((x + 1) * width) / w))
      let sum = 0
      for (let sy = top; sy < bottom; sy++) for (let sx = left; sx < right; sx++) sum += gray[sy * width + sx]
      small[y * w + x] = sum / ((bottom - top) * (right - left))
    }
  }
  return { gray: small, width: w, height: h }
}

/** Premier pixel de la case [index] (ou bord de fin, pour index = GRID) sur un côté de [size] px. */
export const cellEdge = (index, size) => Math.ceil((index * size) / GRID)

// Zone utile, en cases (`right` et `bottom` exclus) : le plus petit rectangle qui contient toutes les cases couvertes, toute
// l'image s'il n'y en a aucune.
const usefulZone = emptyCells => {
  const covered = emptyCells.flatMap((empty, i) => (empty ? [] : [i]))
  if (!covered.length) return { left: 0, top: 0, right: GRID, bottom: GRID }
  const columns = covered.map(i => i % GRID)
  const rows = covered.map(i => Math.floor(i / GRID))
  return { left: Math.min(...columns), top: Math.min(...rows), right: Math.max(...columns) + 1, bottom: Math.max(...rows) + 1 }
}

const crop = (gray, width, left, top, right, bottom) => {
  const cropped = new Uint8ClampedArray((right - left) * (bottom - top))
  for (let y = top; y < bottom; y++) cropped.set(gray.subarray(y * width + left, y * width + right), (y - top) * (right - left))
  return { gray: cropped, width: right - left, height: bottom - top }
}

const contrastOf = gray => {
  let sum = 0
  let squares = 0
  for (let i = 0; i < gray.length; i++) {
    sum += gray[i]
    squares += gray[i] * gray[i]
  }
  const mean = sum / gray.length
  return Math.sqrt(Math.max(0, squares / gray.length - mean * mean))
}

/**
 * Mesures d'une cible : `image` est l'image RGBA ramenée à la taille de travail, `file` décrit le fichier d'origine
 * ({ width, height, mimeType }). Rend l'image en gris, les cases vides et la zone utile en cases (de quoi les montrer), et les
 * mesures que `rateTarget` note.
 */
export const measureTarget = (image, file) => {
  const { width, height } = image
  const gray = toGray(image.rgba)
  const corners = detectCorners(gray, width, height)

  const perCell = new Array(GRID * GRID).fill(0)
  corners.forEach(c => perCell[Math.floor((c.y * GRID) / height) * GRID + Math.floor((c.x * GRID) / width)]++)
  const emptyCells = perCell.map(count => count < CELL_MIN_CORNERS)
  const described = describe(gray, width, height, corners)

  const zone = usefulZone(emptyCells)
  const zoneCells = (zone.right - zone.left) * (zone.bottom - zone.top)
  const zoneImage = crop(
    gray,
    width,
    cellEdge(zone.left, width),
    cellEdge(zone.top, height),
    cellEdge(zone.right, width),
    cellEdge(zone.bottom, height)
  )
  const far = downscale(gray, width, height, FAR_SIDE)

  return {
    file,
    gray,
    width,
    height,
    emptyCells,
    zone,
    described,
    corners: corners.length,
    farCorners: detectCorners(far.gray, far.width, far.height).length,
    zoneArea: zoneCells / emptyCells.length,
    coverage: emptyCells.filter(empty => !empty).length / zoneCells,
    distinctiveness: distinctivenessOf(described),
    contrast: contrastOf(zoneImage.gray)
  }
}

// Points que retire au critère [key] la valeur [value] ; `bad` peut être au-dessus ou au-dessous de `good`.
const penaltyOf = (key, value) => {
  const { bad, good, max } = SCALE[key]
  return Math.round(max * Math.min(1, Math.max(0, (good - value) / (good - bad))))
}

/**
 * Note sur 100, verdict (de `excellent` à `bad`, cf. VERDICTS) et alertes ({ key, penalty, level, values }, de la plus coûteuse à la moins
 * coûteuse) d'une cible mesurée. La note vaut 100 moins la somme des pénalités des alertes ; une alerte est une `error` à partir de
 * ERROR_PENALTY points. [siblings] sont les autres cibles de son module ({ index, measures }, index à partir de 1) : une cible
 * trop proche d'une autre se confond avec elle.
 */
export const rateTarget = (measures, siblings = []) => {
  const { file } = measures
  const alerts = []
  const check = (key, penalty, values = {}) => {
    if (penalty > 0) alerts.push({ key, penalty, level: penalty >= ERROR_PENALTY ? 'error' : 'warning', values })
  }
  const size = { width: file.width, height: file.height }
  const ratio = Math.max(file.width, file.height) / Math.min(file.width, file.height)

  check('format', file.mimeType === 'image/jpeg' ? 0 : SCALE.format.max, {
    format: (file.mimeType || '?').replace('image/', '').toUpperCase()
  })
  check('too-small', penaltyOf('too-small', Math.min(file.width, file.height)), size)
  check('heavy', penaltyOf('heavy', Math.max(file.width, file.height)), size)
  check('stretched', penaltyOf('stretched', ratio), { ratio: ratio.toFixed(1) })
  check('few-corners', penaltyOf('few-corners', measures.corners))
  check('fine-details', penaltyOf('fine-details', measures.farCorners))
  check('small-zone', penaltyOf('small-zone', measures.zoneArea), { percent: Math.round(measures.zoneArea * 100) })
  check('empty-zones', penaltyOf('empty-zones', measures.coverage), { percent: Math.round((1 - measures.coverage) * 100) })
  check('repetitive', penaltyOf('repetitive', measures.distinctiveness))
  check('low-contrast', penaltyOf('low-contrast', measures.contrast))
  siblings.forEach(({ index, measures: other }) => {
    const shared = Math.max(sharedRatio(measures.described, other.described), sharedRatio(other.described, measures.described))
    check('similar', penaltyOf('similar', shared), { index })
  })

  alerts.sort((a, b) => b.penalty - a.penalty)
  const score = Math.max(0, 100 - alerts.reduce((sum, alert) => sum + alert.penalty, 0))
  return { score, verdict: VERDICTS.find(([min]) => score >= min)[1], alerts }
}
